/**
 * The upsell dialog is rendered by the editor UI package, which registers this
 * command. Core code opens it via `ICommandService.executeCommand(OpenUpsellDialogCommandId, options)`
 * and should fall back to a notification when the command is not registered
 * (e.g. headless/agent builds that run without the editor UI).
 */
export const OpenUpsellDialogCommandId = 'upsell.open';

/**
 * Rollout mode for the render-graph pass-inspection gate:
 * - `"shadow"` — pass inspection is FREE while the feature is in beta. The
 *   Inspector shows a "Beta · free for now" badge (pre-announcing the Pro
 *   gate) and gate-hit telemetry fires exactly as if enforced, so the flip
 *   date can be chosen from real usage data instead of intuition.
 * - `"enforced"` — the locked preview (stripes + unlock pitch) replaces the
 *   pass content for unlicensed users.
 * Flip to `"enforced"` when the beta ends: the badge made the change
 * pre-announced, so it's an expected graduation, not a feature removal.
 */
export const PASS_INSPECTION_GATE: "shadow" | "enforced" = "shadow";

export interface UpsellDialogOptions {
    /** Where this upsell was triggered from, e.g. "export_gltf". Used for telemetry. */
    source: string;
    /**
     * Fine-grained trigger for telemetry, e.g. which property was being edited
     * ("edit color uniform on MeshStandardNodeMaterial"). Included in all
     * upsell.* events (shown/action/dismissed/success) so funnels can segment
     * by the exact trigger — including what made people sign up.
     */
    detail?: string;
    title?: string;
    message?: string;
    /** Short plain-text line under the pitch title ("Going deeper is a Pro
     *  feature" → what exactly the user just tried). Falls back to the generic
     *  lede. Unlike `message` this must be plain text (no markdown). */
    subtitle?: string;
    /** Command to execute after a successful login/signup, e.g. to resume the gated action. */
    continueCommand?: { id: string; args?: unknown };
    /** Url opened in a new tab after a successful login/signup. */
    returnUrl?: string;
    /**
     * When true the dialog runs a two-stage flow: signed-out users see
     * login/sign-up first; signed-in users without the entitlement see a
     * "Get Needle Inspector Pro" buy action. The dialog resolves the actual
     * checkout URL live (once the user is known) via the license service.
     * Omit for a plain login gate.
     */
    requiresLicense?: boolean;
    /** Optional override for the checkout URL (else the license service resolves it). */
    purchaseUrl?: string;
    /** Label for the buy action (default "Get Needle Inspector Pro"). */
    purchaseLabel?: string;
    /**
     * Dev PREVIEW: show the dialog regardless of what the user is entitled to,
     * so the sell can be looked at while signed in with Pro. Without it an
     * entitled user sees nothing — the dialog treats "already licensed" as the
     * flow having succeeded, closes instantly, runs `continueCommand` and logs
     * an `upsell.success` with `choice: "buy"`, i.e. a purchase that never
     * happened. A preview therefore also emits NO upsell telemetry at all.
     * Never set this on a real gate.
     */
    preview?: boolean;
}
